import { Button } from "@peer-start/ui/reducate";
import { IconTile } from "@peer-start/ui";
import {
  CalendarDays,
  CircleCheck,
  Compass,
  GraduationCap,
  ListChecks,
  MessagesSquare,
  Route,
  X,
  type LucideIcon,
} from "lucide-react";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import type { ProductTourStep } from "./product-tour-steps.js";

type IconTone = "primary" | "info" | "warning" | "muted";

const stepIcons: Record<ProductTourStep["id"], { icon: LucideIcon; tone: IconTone }> = {
  welcome: { icon: Compass, tone: "primary" },
  competencies: { icon: Route, tone: "primary" },
  skills: { icon: GraduationCap, tone: "primary" },
  tasks: { icon: ListChecks, tone: "muted" },
  progress: { icon: CircleCheck, tone: "info" },
  mentor: { icon: CalendarDays, tone: "primary" },
  chat: { icon: MessagesSquare, tone: "info" },
};

function StepIllustration({ stepId }: { stepId: ProductTourStep["id"] }) {
  const { icon: Icon, tone } = stepIcons[stepId];
  return (
    <div className="flex items-center gap-3 rounded-card border border-border/70 bg-muted/40 p-4">
      <IconTile tone={tone}>
        <Icon aria-hidden="true" className="size-5" strokeWidth={1.8} />
      </IconTile>
      <div className="min-w-0 flex-1 space-y-2" aria-hidden="true">
        <div className="h-2.5 w-3/4 rounded-full bg-foreground/15" />
        <div className="h-2 w-1/2 rounded-full bg-foreground/10" />
      </div>
    </div>
  );
}

export function ProductTourTip({
  stepId,
  title,
  body,
  progressLabel,
  primaryControlId,
  primaryLabel,
  secondaryControlId,
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
  position,
  centered = false,
  tipRef,
}: {
  stepId: ProductTourStep["id"];
  title: string;
  body: string;
  progressLabel: string;
  primaryControlId: string;
  primaryLabel: string;
  secondaryControlId: string;
  secondaryLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
  onClose: () => void;
  position: { top: number; left: number };
  centered?: boolean;
  tipRef: Ref<HTMLElement>;
}) {
  const { t } = useTranslation();
  const titleId = "product-tour-title";
  return (
    <section
      aria-labelledby={titleId}
      aria-modal="true"
      className={`${centered ? "relative z-10" : "absolute z-10"} flex flex-col overflow-hidden rounded-dialog border border-border bg-card text-foreground shadow-xl w-[min(100%-0.5rem,22.5rem)]`}
      ref={tipRef}
      role="dialog"
      style={centered ? undefined : { top: position.top, left: position.left }}
    >
      <header className="flex items-start justify-between gap-3 px-4 pt-4">
        <p className="text-xs font-medium uppercase tracking-wide">{progressLabel}</p>
        <Button
          aria-label={t("productTour.close")}
          className="rounded-button p-1.5 text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
          controlId="product-tour.close"
          variant="ghost"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      </header>
      <div className="space-y-4 px-4 pb-4 pt-3">
        <StepIllustration stepId={stepId} />
        <div>
          <h2 className="text-lg font-medium leading-snug" id={titleId}>
            {title}
          </h2>
          <p className="mt-2 text-sm leading-relaxed">{body}</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <Button controlId={secondaryControlId} onClick={onSecondary} type="button" variant="secondary">
            {secondaryLabel}
          </Button>
          <Button controlId={primaryControlId} onClick={onPrimary} type="button">
            {primaryLabel}
          </Button>
        </div>
      </div>
    </section>
  );
}
